import { useState, useEffect } from 'react';
import { isWoo, getAuthHeaders } from '../lib/auth';
import { GateBanner } from '../components/GateBanner';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  SkeletonBodyText,
  Select,
  ProgressBar,
  InlineGrid,
  Box,
  Button,
} from '@shopify/polaris';

/** Turn a non-OK response into an Error carrying the machine-readable gate
 *  fields, so GateBanner can offer the upgrade path instead of a dead end.
 *  This page uses raw fetch (it predates fetchApi's typed ApiError). */
async function toGateError(res: Response): Promise<Error> {
  let body: { error?: string; code?: string; requiredPlan?: string; currentPlan?: string } = {};
  try {
    body = await res.json();
  } catch {
    // Non-JSON body (proxy error page): the status alone has to carry it.
  }
  const err = new Error(body.error || `Could not load analytics (HTTP ${res.status})`);
  return Object.assign(err, {
    code: body.code,
    requiredPlan: body.requiredPlan,
    currentPlan: body.currentPlan,
  });
}

/**
 * The one view no ad platform can produce for the merchant: Google cannot see
 * that Meta is claiming the same order. Every number is countable from our own
 * conversions - deliberately NOT an estimate of what each platform will report,
 * since they also claim view-through and PII matches we never see.
 */
interface ChannelOverlap {
  orders: number;
  claimableByChannel: Array<{ channel: string; orders: number; revenue: number }>;
  multiClaimedOrders: number;
  multiClaimedRevenue: number;
  combinations: Array<{ channels: string[]; orders: number }>;
  totalClaims: number;
}

interface AttributionData {
  channel: string;
  conversions: number;
  revenue: number;
  percentage: number;
}

interface FunnelStage {
  stage: string;
  count: number;
  dropoff: number;
}

interface ConversionLagData {
  averageLagHours: number;
  medianLagHours: number;
  distribution: Array<{ bucket: string; count: number }>;
}

export function Analytics() {
  const [days, setDays] = useState('30');
  const [attribution, setAttribution] = useState<AttributionData[] | null>(null);
  const [overlap, setOverlap] = useState<ChannelOverlap | null>(null);
  const [funnel, setFunnel] = useState<FunnelStage[] | null>(null);
  const [lag, setLag] = useState<ConversionLagData | null>(null);
  const [loading, setLoading] = useState(true);

  const [embedError, setEmbedError] = useState<string | null>(null);
  const [gateError, setGateError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setGateError(null);
      try {
        // S13: explicit guard — fail with a visible message rather than
        // silently 401 when opened outside the Shopify admin embed. Skipped
        // for WooCommerce (cookie auth).
        if (!isWoo() && typeof window.shopify?.idToken !== 'function') {
          setEmbedError('Trackful must be opened from the Shopify admin.');
          setLoading(false);
          return;
        }
        const authHeaders = await getAuthHeaders();
        const headers: Record<string, string> = { 'Content-Type': 'application/json', ...authHeaders };

        const [attrRes, funnelRes, lagRes] = await Promise.all([
          fetch(`/api/analytics/attribution?days=${days}`, { headers, credentials: 'include' }),
          fetch(`/api/analytics/funnel?days=${days}`, { headers, credentials: 'include' }),
          fetch(`/api/analytics/conversion-lag?days=${days}`, { headers, credentials: 'include' }),
        ]);

        // Every /api/analytics route sits behind requireFeature('advanced_analytics').
        // These three checks used to have no else, so a plan without it got a
        // page of zeroes with no error and no upgrade path - indistinguishable
        // from a store that simply has no data yet.
        if (!attrRes.ok) {
          setGateError(await toGateError(attrRes));
          setLoading(false);
          return;
        }

        const data = await attrRes.json();
        setAttribution(data.breakdown);
        setOverlap(data.overlap ?? null);

        if (funnelRes.ok) {
          const funnelData = await funnelRes.json();
          setFunnel(funnelData.funnel);
        }
        if (lagRes.ok) {
          setLag(await lagRes.json());
        }
      } catch (err) {
        setEmbedError(err instanceof Error ? err.message : 'Could not load analytics');
      }
      setLoading(false);
    };

    fetchData();
  }, [days]);

  if (embedError) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <p>{embedError}</p>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section><Card><SkeletonBodyText lines={8} /></Card></Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (gateError) {
    return (
      <Page title="Analytics" subtitle="Attribution, funnel analysis, and conversion insights">
        <Layout>
          <Layout.Section>
            <GateBanner error={gateError} />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const handleExportCSV = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/dashboard/export/csv?days=${days}`, { headers, credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trackful-conversions-${days}d.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* silently fail */ }
  };

  return (
    <Page
      title="Analytics"
      subtitle="Attribution, funnel analysis, and conversion insights"
      primaryAction={
        <InlineStack gap="300">
          <Select
            label=""
            labelInline
            options={[
              { label: 'Last 7 days', value: '7' },
              { label: 'Last 30 days', value: '30' },
              { label: 'Last 60 days', value: '60' },
              { label: 'Last 90 days', value: '90' },
            ]}
            value={days}
            onChange={setDays}
          />
          <Button onClick={handleExportCSV}>Export CSV</Button>
        </InlineStack>
      }
    >
      <Layout>
        {/* ---------------------------------------------------------------
            Channel overlap. THE differentiating view: we are the only party
            that sees one conversion carrying every network's click id at
            once, so we are the only one who can tell the merchant why the
            sum of their platform dashboards exceeds their real order count.
            Google cannot see that Meta is claiming the same sale.
            --------------------------------------------------------------- */}
        {overlap && overlap.orders > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Who claims your orders</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Each ad platform counts a sale it can attribute to itself, and none of
                    them can see the others. This is the same period seen from our side,
                    where every claim is visible at once.
                  </Text>
                </BlockStack>

                <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
                  <BlockStack gap="050">
                    <Text as="p" variant="headingLg">{overlap.orders}</Text>
                    <Text as="p" variant="bodySm" tone="subdued">orders you actually had</Text>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="headingLg" tone={overlap.totalClaims > overlap.orders ? 'critical' : undefined}>
                      {overlap.totalClaims}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">claims across all platforms</Text>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="headingLg">{overlap.multiClaimedOrders}</Text>
                    <Text as="p" variant="bodySm" tone="subdued">orders claimed more than once</Text>
                  </BlockStack>
                </InlineGrid>

                {overlap.claimableByChannel.length > 0 && (
                  <BlockStack gap="300">
                    {overlap.claimableByChannel.map((c) => (
                      <BlockStack key={c.channel} gap="100">
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">{c.channel}</Text>
                          <Text as="span" variant="bodySm">
                            {`can claim ${c.orders} of ${overlap.orders}`}
                          </Text>
                        </InlineStack>
                        <ProgressBar
                          progress={Math.min(100, Math.round((c.orders / overlap.orders) * 100))}
                          size="small"
                        />
                      </BlockStack>
                    ))}
                  </BlockStack>
                )}

                {overlap.combinations.length > 0 && (
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <BlockStack gap="200">
                      {overlap.combinations.map((combo) => (
                        <Text as="p" variant="bodySm" key={combo.channels.join('|')}>
                          <strong>{combo.orders}</strong>{' '}
                          {combo.orders === 1 ? 'order carries' : 'orders carry'} a click ID from{' '}
                          {combo.channels.join(', ')}
                        </Text>
                      ))}
                    </BlockStack>
                  </Box>
                )}

                <Text as="p" variant="bodySm" tone="subdued">
                  These are orders whose visitor arrived through more than one network, so
                  more than one will report the sale. We count what we can see: a platform
                  may also claim a sale with no click ID at all, through a view or a data
                  match, and no tool can see those.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Attribution Breakdown */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Attribution by Channel</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  One channel per order, so this adds up to your real order count. When an
                  order carries several click IDs we credit the first match in this order:
                  Google Ads, Meta, TikTok, Pinterest, Snapchat.
                </Text>
              </BlockStack>
              {attribution && attribution.length > 0 ? (
                <BlockStack gap="300">
                  {attribution.map((a) => (
                    <BlockStack key={a.channel} gap="100">
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">{a.channel}</Text>
                        <InlineStack gap="200">
                          <Text as="span" variant="bodySm">{a.conversions} conv.</Text>
                          <Badge tone={a.percentage >= 30 ? 'success' : a.percentage >= 10 ? 'info' : 'attention'}>
                            {`${a.percentage}%`}
                          </Badge>
                        </InlineStack>
                      </InlineStack>
                      <ProgressBar progress={a.percentage} size="small" />
                    </BlockStack>
                  ))}
                </BlockStack>
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">No conversion data for this period.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Funnel Analysis */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Conversion Funnel</Text>
              {funnel && funnel.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text']}
                  headings={['Stage', 'Count', 'Drop-off']}
                  rows={funnel.map((s) => [
                    s.stage,
                    s.count.toLocaleString(),
                    s.dropoff > 0 ? `${s.dropoff}%` : '—',
                  ])}
                />
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">No funnel data available.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Conversion Lag */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Conversion Lag</Text>
              {lag ? (
                <BlockStack gap="300">
                  <InlineGrid columns={2} gap="400">
                    <Box>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" tone="subdued">Average</Text>
                        <Text as="p" variant="headingLg">{formatLag(lag.averageLagHours)}</Text>
                      </BlockStack>
                    </Box>
                    <Box>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" tone="subdued">Median</Text>
                        <Text as="p" variant="headingLg">{formatLag(lag.medianLagHours)}</Text>
                      </BlockStack>
                    </Box>
                  </InlineGrid>
                  {lag.distribution.length > 0 && (
                    <DataTable
                      columnContentTypes={['text', 'numeric']}
                      headings={['Time Range', 'Conversions']}
                      rows={lag.distribution.filter(d => d.count > 0).map((d) => [d.bucket, d.count])}
                    />
                  )}
                </BlockStack>
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">No data available.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function formatLag(hours: number): string {
  if (hours < 1 / 60) return '< 1 sec';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  return `${(hours / 24).toFixed(1)} days`;
}
